import { useSelector } from 'react-redux'
import Button from '../../../components/Button/Button'
import { acceptsAch } from '../../../constants/paymentMethods'
import { selectFeatureFlag } from '../../../features/featureFlags/selectors'
import { DEBT_AMOUNT_DECIMALS } from '../../../config/featureFlagKeys'

const ReviewStep = ({
  personalInfo,
  selectedDebts,
  selectedTerm,
  frequency,
  config,
  onSubmit,
  onBack,
  submitting,
}) => {
  const amountDecimals = useSelector(selectFeatureFlag(DEBT_AMOUNT_DECIMALS))
  const totalDebt = selectedDebts.reduce((sum, d) => sum + (d.balanceAmount || 0), 0)
  const programFeeAmount = totalDebt * (config.programFee / 100)
  const numberOfPayments = frequency === 'biweekly' ? selectedTerm * 2 : selectedTerm
  const paymentAmount = programFeeAmount / numberOfPayments
  const addedDetails = acceptsAch(config.acceptedPaymentMethods) ? 'bank account' : 'payment details'

  return (
    <>
      <div className="border border-divider bg-surface shadow-lg rounded-2xl w-full">
        <div className="font-bold text-lg md:text-xl-20 pt-4 md:pt-6 pl-4 md:pl-12 pb-3 md:pb-4">
          Personal Information
        </div>
        <div className="border-b border-divider mx-4 md:mx-6" />
        <div className="px-4 md:px-12 py-4 md:py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4 text-sm">
            <div className="flex justify-between py-2 border-b border-divider">
              <span className="text-text-secondary">Name:</span>
              <span className="font-medium text-text-primary">{personalInfo.firstName} {personalInfo.lastName}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-divider">
              <span className="text-text-secondary">Email:</span>
              <span className="font-medium text-text-primary break-all">{personalInfo.email}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-divider">
              <span className="text-text-secondary">Phone:</span>
              <span className="font-medium text-text-primary">{personalInfo.primaryPhone}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-divider">
              <span className="text-text-secondary">Date of Birth:</span>
              <span className="font-medium text-text-primary">{personalInfo.dateOfBirth}</span>
            </div>
            {personalInfo.gender && (
              <div className="flex justify-between py-2 border-b border-divider">
                <span className="text-text-secondary">Gender:</span>
                <span className="font-medium text-text-primary capitalize">{personalInfo.gender}</span>
              </div>
            )}
            {personalInfo.address && (
              <div className="flex justify-between py-2 border-b border-divider md:col-span-2">
                <span className="text-text-secondary whitespace-nowrap mr-4">Address:</span>
                <span className="font-medium text-text-primary text-right">
                  {personalInfo.address}
                  {personalInfo.city && `, ${personalInfo.city}`}
                  {personalInfo.state && `, ${personalInfo.state}`}
                  {personalInfo.zip && ` ${personalInfo.zip}`}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="border border-divider bg-surface shadow-lg rounded-2xl w-full mt-4 md:mt-6">
        <div className="font-bold text-lg md:text-xl-20 pt-4 md:pt-6 pl-4 md:pl-12 pb-3 md:pb-4">
          Enrolled Debts
        </div>
        <div className="border-b border-divider mx-4 md:mx-6" />
        <div className="px-4 md:px-12 py-4 md:py-6">
          {selectedDebts.map((debt) => (
            <div key={debt._id} className="flex justify-between py-3 border-b border-divider">
              <span className="text-text-primary break-all mr-2">{debt.creditorName}</span>
              <span className="font-medium text-text-primary whitespace-nowrap">
                ${debt.balanceAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </span>
            </div>
          ))}
          <div className="flex justify-between py-3 mt-2">
            <span className="font-medium text-text-primary">Total Debt</span>
            <span className="font-bold text-primary-main text-base md:text-lg">
              ${totalDebt.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      </div>

      <div className="border border-divider bg-surface shadow-lg rounded-2xl w-full mt-4 md:mt-6">
        <div className="font-bold text-lg md:text-xl-20 pt-4 md:pt-6 pl-4 md:pl-12 pb-3 md:pb-4">
          Program Details
        </div>
        <div className="border-b border-divider mx-4 md:mx-6" />
        <div className="px-4 md:px-12 py-4 md:py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4 text-sm">
            <div className="flex justify-between py-2 border-b border-divider">
              <span className="text-text-secondary">Program Length:</span>
              <span className="font-medium text-text-primary">{selectedTerm} months ({numberOfPayments} payments)</span>
            </div>
            <div className="flex justify-between py-2 border-b border-divider">
              <span className="text-text-secondary">{frequency === 'biweekly' ? 'Bi-Weekly' : 'Monthly'} Payment:</span>
              <span className="font-medium text-primary-main text-base md:text-lg">${paymentAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-divider">
              <span className="text-text-secondary">Program Fee ({config.programFee}%):</span>
              <span className="font-medium text-text-primary">${(amountDecimals ? Number(programFeeAmount.toFixed(2)) : programFeeAmount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="border border-divider bg-surface shadow-lg rounded-2xl w-full mt-4 md:mt-6">
        <div className="px-4 md:px-12 py-4 md:py-6">
          <p className="text-text-secondary text-sm">
            By clicking "Submit & Continue", you agree to enroll in the debt relief program.
            You will be redirected to add your {addedDetails} and sign the agreement.
          </p>
        </div>
      </div>

      <div className="mt-4 md:mt-6 mb-6 flex flex-col-reverse sm:flex-row justify-between gap-3 w-full">
        <Button type="outlined" width="w-full sm:min-w-[156px] sm:w-auto" onClick={onBack} disabled={submitting}>
          Back
        </Button>
        <Button width="w-full sm:min-w-[156px] sm:w-auto" onClick={onSubmit} disabled={submitting}>
          {submitting ? 'Submitting...' : 'Submit & Continue'}
        </Button>
      </div>
    </>
  )
}

export default ReviewStep
